import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { Box, CircularProgress, GlobalStyles, Stack, Typography } from '@mui/material';
import { rotuloPrioridade } from './kmzFicha';
import { representanteDoServico } from './kmzGeo';
import { garantirIconesKmz } from './kmzIcones';
import KmzFotoPainel from './KmzFotoPainel';
import KmzMapaControles from './KmzMapaControles';
import { filtroComConsulta } from './kmzConsulta';
import { SATELLITE_STYLE, STYLE_URL } from './KmzRecorteMap';

const FILTRO_LINHA = ['in', ['geometry-type'], ['literal', ['LineString', 'MultiLineString']]];
const BASES_KMZ = {
  'kmz-casing': FILTRO_LINHA,
  'kmz-linhas': FILTRO_LINHA,
  'kmz-hit-linhas': FILTRO_LINHA,
  'kmz-hit-pontos': ['==', ['geometry-type'], 'Point'],
  'kmz-pontos': ['all',
    ['==', ['geometry-type'], 'Point'],
    ['!', ['to-boolean', ['get', 'iconeUrl']]],
  ],
  'kmz-icones': ['all',
    ['==', ['geometry-type'], 'Point'],
    ['to-boolean', ['get', 'iconeUrl']],
  ],
  'kmz-rotulos': ['all',
    ['==', ['geometry-type'], 'Point'],
    ['!=', ['coalesce', ['get', 'nome'], ''], ''],
  ],
};
const CAMADAS_PONTO = {
  'kmz-hit-pontos': true,
  'kmz-pontos': true,
  'kmz-icones': true,
};

function aplicarConsulta(map, consulta, etiquetas) {
  Object.keys(BASES_KMZ).forEach((id) => {
    if (!map.getLayer(id)) return;
    map.setFilter(id, filtroComConsulta(BASES_KMZ[id], consulta, Boolean(CAMADAS_PONTO[id]), etiquetas));
  });
}
const COR = ['coalesce', ['get', 'corResolvida'], '#808080'];
const LARGURA = ['coalesce', ['to-number', ['get', 'largura']], 4];

const CAMADAS_CLIQUE = ['kmz-hit-pontos', 'kmz-rotulos', 'kmz-icones', 'kmz-pontos', 'kmz-hit-linhas'];

function botaoFechar(fechar) {
  const close = document.createElement('button');
  close.type = 'button';
  close.setAttribute('aria-label', 'Fechar');
  close.textContent = '×';
  close.style.width = '32px';
  close.style.height = '32px';
  close.style.margin = '-6px -6px -6px 0';
  close.style.padding = '0';
  close.style.border = 'none';
  close.style.borderRadius = '8px';
  close.style.background = 'transparent';
  close.style.color = '#111827';
  close.style.fontSize = '22px';
  close.style.lineHeight = '32px';
  close.style.cursor = 'pointer';
  close.style.flexShrink = '0';
  close.addEventListener('click', (event) => {
    event.stopPropagation();
    fechar();
  });
  return close;
}

function lerFicha(props) {
  if (!props || !props.ficha) return null;
  try {
    const ficha = JSON.parse(props.ficha);
    return ficha && typeof ficha === 'object' ? ficha : null;
  } catch (error) {
    return null;
  }
}

function fichaAbre(feature) {
  const ficha = lerFicha(feature && feature.properties);
  return !ficha || ficha.abrir !== false;
}

function faixaDeCampos(campos) {
  const faixa = document.createElement('div');
  faixa.style.display = 'flex';
  faixa.style.flexWrap = 'wrap';
  faixa.style.gap = '8px 12px';
  (campos || []).forEach((campo) => {
    if (!campo || !campo.valor) return;
    const item = document.createElement('div');
    item.style.flex = campo.cheia ? '1 1 100%' : '1 1 120px';
    item.style.minWidth = campo.cheia ? '100%' : '0';
    item.style.maxWidth = '100%';
    const label = document.createElement('div');
    label.textContent = campo.rotulo;
    label.style.fontSize = '11px';
    label.style.lineHeight = '1.3';
    label.style.color = '#6b7280';
    const value = document.createElement('div');
    value.textContent = campo.valor;
    value.style.fontSize = '13px';
    value.style.lineHeight = '1.35';
    value.style.overflowWrap = 'anywhere';
    item.appendChild(label);
    item.appendChild(value);
    faixa.appendChild(item);
  });
  return faixa;
}

function estiloPrioridade(valor) {
  const texto = String(valor || '').trim().toUpperCase();
  if (texto === 'E' || texto === '1') return { fundo: '#fef2f2', cor: '#b91c1c' };
  if (texto === '2') return { fundo: '#fff7ed', cor: '#c2410c' };
  return { fundo: '#f3f4f6', cor: '#374151' };
}

function cartaoDeServico(item, primeiro) {
  const card = document.createElement('div');
  card.style.display = 'flex';
  card.style.alignItems = 'flex-start';
  card.style.gap = '8px';
  card.style.padding = '8px 0';
  if (!primeiro) card.style.borderTop = '1px solid #e5e7eb';
  if (item.prioridade) {
    const selo = document.createElement('span');
    const cores = estiloPrioridade(item.prioridade);
    selo.textContent = rotuloPrioridade(item.prioridade);
    selo.style.flexShrink = '0';
    selo.style.marginTop = '1px';
    selo.style.padding = '0 6px';
    selo.style.borderRadius = '4px';
    selo.style.background = cores.fundo;
    selo.style.color = cores.cor;
    selo.style.fontSize = '11px';
    selo.style.fontWeight = '700';
    selo.style.lineHeight = '18px';
    card.appendChild(selo);
  }
  const texto = document.createElement('div');
  texto.style.minWidth = '0';
  texto.style.flex = '1';
  const nome = document.createElement('div');
  nome.textContent = item.nome;
  nome.style.fontSize = '13px';
  nome.style.fontWeight = '600';
  nome.style.lineHeight = '1.35';
  nome.style.overflowWrap = 'anywhere';
  texto.appendChild(nome);
  if (item.descricao) {
    const descricao = document.createElement('div');
    descricao.textContent = item.descricao;
    descricao.style.marginTop = '2px';
    descricao.style.fontSize = '12px';
    descricao.style.lineHeight = '1.35';
    descricao.style.color = '#4b5563';
    descricao.style.overflowWrap = 'anywhere';
    texto.appendChild(descricao);
  }
  card.appendChild(texto);
  return card;
}

function secao(titulo, aberto, contagem, montar) {
  const details = document.createElement('details');
  details.open = aberto;
  details.addEventListener('click', (event) => event.stopPropagation());
  const summary = document.createElement('summary');
  summary.textContent = contagem ? `${titulo} (${contagem})` : titulo;
  summary.style.cursor = 'pointer';
  summary.style.fontSize = '12px';
  summary.style.fontWeight = '600';
  summary.style.marginTop = '8px';
  details.appendChild(summary);
  const corpo = document.createElement('div');
  corpo.style.marginTop = '6px';
  montar(corpo);
  details.appendChild(corpo);
  return details;
}

function botaoAbrir(onClick) {
  const botao = document.createElement('button');
  botao.type = 'button';
  botao.setAttribute('aria-label', 'Abrir serviço');
  botao.style.width = '22px';
  botao.style.height = '22px';
  botao.style.padding = '0';
  botao.style.border = '1px solid #e5e7eb';
  botao.style.borderRadius = '4px';
  botao.style.background = 'transparent';
  botao.style.color = '#c2410c';
  botao.style.cursor = 'pointer';
  botao.style.flexShrink = '0';
  botao.innerHTML = '<svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true"><path d="M4 2H2.5A1.5 1.5 0 0 0 1 3.5v6A1.5 1.5 0 0 0 2.5 11h6A1.5 1.5 0 0 0 10 9.5V8" fill="none" stroke="currentColor" stroke-width="1.2"/><path d="M6 1h5v5" fill="none" stroke="currentColor" stroke-width="1.2"/><path d="M11 1L5.5 6.5" fill="none" stroke="currentColor" stroke-width="1.2"/></svg>';
  botao.addEventListener('click', (event) => {
    event.stopPropagation();
    onClick();
  });
  return botao;
}

function miniatura(url, onClick) {
  const botao = document.createElement('button');
  botao.type = 'button';
  botao.style.width = '72px';
  botao.style.height = '72px';
  botao.style.padding = '0';
  botao.style.border = '1px solid #e5e7eb';
  botao.style.borderRadius = '6px';
  botao.style.overflow = 'hidden';
  botao.style.cursor = 'pointer';
  botao.style.background = '#f3f4f6';
  botao.style.flexShrink = '0';
  const img = document.createElement('img');
  img.src = url;
  img.alt = '';
  img.style.width = '100%';
  img.style.height = '100%';
  img.style.objectFit = 'cover';
  img.style.display = 'block';
  botao.appendChild(img);
  botao.addEventListener('click', (event) => {
    event.stopPropagation();
    onClick();
  });
  return botao;
}

function faixaDeFotos(fotos, onFoto, ficha) {
  const faixa = document.createElement('div');
  faixa.style.display = 'flex';
  faixa.style.gap = '8px';
  faixa.style.flexWrap = 'wrap';
  fotos.forEach((url, indice) => {
    faixa.appendChild(miniatura(url, () => onFoto(ficha, indice)));
  });
  return faixa;
}

function secaoServico(frases, extras, explicacaoPrimeiro) {
  return secao('Serviço', explicacaoPrimeiro, frases.length, (corpo) => {
    const montarFrases = () => {
      frases.forEach((item, indice) => {
        corpo.appendChild(cartaoDeServico(item, indice === 0 && (explicacaoPrimeiro || !extras.length)));
      });
    };
    if (explicacaoPrimeiro) {
      montarFrases();
      if (extras.length) corpo.appendChild(faixaDeCampos(extras));
      return;
    }
    if (extras.length) corpo.appendChild(faixaDeCampos(extras));
    montarFrases();
  });
}

function popupDaEstrutura(feature, fechar, onFoto, onAbrir, faixa) {
  const props = (feature && feature.properties) || {};
  const tipoGeo = feature && feature.geometry ? feature.geometry.type : '';
  const ehLinha = tipoGeo === 'LineString' || tipoGeo === 'MultiLineString';
  const ficha = lerFicha(props);
  const nome = props.nome ? String(props.nome) : '';
  const root = document.createElement('div');
  root.style.color = '#111827';
  root.style.fontSize = '14px';
  root.style.minWidth = '240px';
  root.style.maxWidth = '320px';
  root.style.maxHeight = '360px';
  root.style.overflowY = 'auto';
  root.style.overflowX = 'hidden';
  const header = document.createElement('div');
  header.style.display = 'flex';
  header.style.alignItems = 'flex-start';
  header.style.justifyContent = 'space-between';
  header.style.gap = '8px';
  const tituloGrupo = document.createElement('div');
  tituloGrupo.style.display = 'flex';
  tituloGrupo.style.alignItems = 'center';
  tituloGrupo.style.gap = '8px';
  tituloGrupo.style.minWidth = '0';
  const title = document.createElement('div');
  title.textContent = (ficha && ficha.titulo) || nome || (ehLinha ? 'Trecho' : 'Estrutura');
  title.style.fontWeight = '700';
  title.style.fontSize = '14px';
  title.style.lineHeight = '1.3';
  tituloGrupo.appendChild(title);
  if (ficha && ficha.nsmpId && onAbrir) tituloGrupo.appendChild(botaoAbrir(() => onAbrir(ficha.nsmpId)));
  header.appendChild(tituloGrupo);
  header.appendChild(botaoFechar(fechar));
  root.appendChild(header);
  const subtitulo = ficha && ficha.tipo
    ? ficha.tipo
    : (nome && !ficha ? (ehLinha ? 'Trecho' : 'Estrutura') : '');
  if (subtitulo) {
    const kind = document.createElement('div');
    kind.textContent = subtitulo;
    kind.style.marginTop = '2px';
    kind.style.fontSize = '12px';
    kind.style.color = '#4b5563';
    root.appendChild(kind);
  }
  if (ficha && ficha.servico) {
    const frases = ficha.servicos || [];
    const extras = ficha.servicoCampos || [];
    const secaoDigitacao = () => {
      if (!(ficha.digitacao || []).length) return;
      root.appendChild(secao('Digitação', true, 0, (corpo) => {
        corpo.appendChild(faixaDeCampos(ficha.digitacao));
      }));
    };
    if (!faixa) secaoDigitacao();
    if (frases.length || extras.length) root.appendChild(secaoServico(frases, extras, Boolean(faixa)));
    if (faixa) secaoDigitacao();
    if ((ficha.poste || []).length) {
      root.appendChild(secao('Estrutura do poste', false, 0, (corpo) => {
        corpo.appendChild(faixaDeCampos(ficha.poste));
      }));
    }
  } else {
    ((ficha && ficha.textos) || []).forEach((texto) => {
      const bloco = document.createElement('div');
      bloco.textContent = texto;
      bloco.style.marginTop = '6px';
      bloco.style.fontSize = '13px';
      bloco.style.lineHeight = '1.35';
      bloco.style.overflowWrap = 'anywhere';
      root.appendChild(bloco);
    });
    if (ficha && (ficha.campos || []).length) root.appendChild(faixaDeCampos(ficha.campos));
  }
  const fotos = (ficha && ficha.fotos) || [];
  if (fotos.length) {
    const montarFotos = (corpo) => corpo.appendChild(faixaDeFotos(fotos, onFoto, ficha));
    if (ficha && ficha.servico) root.appendChild(secao('Fotos', true, fotos.length, montarFotos));
    else {
      const faixa = faixaDeFotos(fotos, onFoto, ficha);
      faixa.style.marginTop = '8px';
      root.appendChild(faixa);
    }
  }
  return root;
}

function featureDoClique(map, point, features) {
  const layers = CAMADAS_CLIQUE.filter((id) => map.getLayer(id));
  if (!layers.length) return null;
  const hits = map.queryRenderedFeatures(point, { layers }).filter(fichaAbre);
  const ponto = hits.find((item) => item.layer && item.layer.id !== 'kmz-hit-linhas');
  const linha = hits.find((item) => item.layer && item.layer.id === 'kmz-hit-linhas');
  const escolhida = ponto || linha || null;
  if (!escolhida) return null;
  return representanteDoServico(features, escolhida);
}

function enquadrar(map, alvo) {
  if (!map || !alvo || !alvo.bounds) return;
  if (alvo.pontoUnico) {
    map.easeTo({ center: alvo.bounds[0], zoom: 16, bearing: 0, pitch: 0 });
    return;
  }
  map.fitBounds(alvo.bounds, { padding: 48 });
}

function garantirCamadas(map, satellite, mostrarInfo) {
  if (!map.getStyle() || map.getSource('kmz')) return;
  map.addSource('kmz', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
  map.addLayer({
    id: 'kmz-casing',
    type: 'line',
    source: 'kmz',
    filter: FILTRO_LINHA,
    paint: satellite
      ? { 'line-color': '#0f172a', 'line-width': ['+', LARGURA, 3], 'line-opacity': 0.8 }
      : { 'line-color': '#9aa0a6', 'line-width': 0, 'line-opacity': 0 },
  });
  map.addLayer({
    id: 'kmz-linhas',
    type: 'line',
    source: 'kmz',
    filter: FILTRO_LINHA,
    paint: { 'line-color': COR, 'line-width': LARGURA, 'line-opacity': 0.95 },
  });
  map.addLayer({
    id: 'kmz-hit-linhas',
    type: 'line',
    source: 'kmz',
    filter: FILTRO_LINHA,
    paint: { 'line-color': '#000000', 'line-width': 16, 'line-opacity': 0.01 },
  });
  map.addLayer({
    id: 'kmz-hit-pontos',
    type: 'circle',
    source: 'kmz',
    filter: ['==', ['geometry-type'], 'Point'],
    paint: { 'circle-radius': 14, 'circle-color': '#000000', 'circle-opacity': 0.01 },
  });
  map.addLayer({
    id: 'kmz-pontos',
    type: 'circle',
    source: 'kmz',
    filter: ['all',
      ['==', ['geometry-type'], 'Point'],
      ['!', ['to-boolean', ['get', 'iconeUrl']]],
    ],
    paint: {
      'circle-radius': 4.5,
      'circle-color': COR,
      'circle-stroke-width': 2,
      'circle-stroke-color': '#ffffff',
    },
  });
  map.addLayer({
    id: 'kmz-icones',
    type: 'symbol',
    source: 'kmz',
    filter: ['all',
      ['==', ['geometry-type'], 'Point'],
      ['to-boolean', ['get', 'iconeUrl']],
    ],
    layout: {
      'icon-image': ['concat', ['get', 'iconeUrl'], '|', COR],
      'icon-size': 1,
      'icon-anchor': 'center',
      'icon-allow-overlap': true,
      'icon-ignore-placement': true,
      'symbol-sort-key': ['coalesce', ['get', 'ordemIcone'], 1],
    },
  });
  map.addLayer({
    id: 'kmz-rotulos',
    type: 'symbol',
    source: 'kmz',
    filter: ['all',
      ['==', ['geometry-type'], 'Point'],
      ['!=', ['coalesce', ['get', 'nome'], ''], ''],
    ],
    layout: {
      'text-field': ['get', 'nome'],
      'text-font': ['Noto Sans Regular'],
      'text-size': 12,
      'text-anchor': 'top',
      'text-offset': [0, 0.8],
      'text-allow-overlap': true,
      'text-ignore-placement': true,
      visibility: mostrarInfo ? 'visible' : 'none',
    },
    paint: {
      'text-color': '#ffffff',
      'text-halo-color': '#111827',
      'text-halo-width': 1.4,
    },
  });
}

const KmzVisualizacaoMap = ({
  ativo,
  geo,
  fullScreen,
  onToggleFullScreen,
  nsirId,
  consulta,
  camera,
  faixa,
}) => {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const popupRef = useRef(null);
  const geoRef = useRef(null);
  const iconTokenRef = useRef(0);
  const basemapRef = useRef('satellite');
  const [basemap, setBasemap] = useState('satellite');
  const [mostrarInfo, setMostrarInfo] = useState(true);
  const mostrarInfoRef = useRef(true);
  const consultaRef = useRef(consulta);
  consultaRef.current = consulta;
  const [tilesLoading, setTilesLoading] = useState(false);
  const [foto, setFoto] = useState(null);
  const nsirIdRef = useRef(nsirId);
  nsirIdRef.current = nsirId;
  const faixaRef = useRef(faixa);
  faixaRef.current = faixa;
  const abrirFotoRef = useRef(() => {});
  const abrirServicoRef = useRef(() => {});
  abrirFotoRef.current = (ficha, indice) => setFoto({ ficha, indice });
  abrirServicoRef.current = (nsmpId) => {
    if (!nsirIdRef.current || !nsmpId) return;
    const url = `/v2/nsir/${nsirIdRef.current}?nsmpId=${encodeURIComponent(nsmpId)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };
  geoRef.current = geo;
  basemapRef.current = basemap;
  mostrarInfoRef.current = mostrarInfo;

  const pintarIcones = (map, collection) => {
    const token = iconTokenRef.current + 1;
    iconTokenRef.current = token;
    garantirIconesKmz(map, collection, () => token === iconTokenRef.current).catch(() => {});
  };

  useEffect(() => {
    if (ativo) return;
    setBasemap('satellite');
    setMostrarInfo(true);
    setTilesLoading(false);
    setFoto(null);
  }, [ativo]);

  useEffect(() => {
    if (!ativo || !containerRef.current) return undefined;
    const element = containerRef.current;
    const map = new maplibregl.Map({
      container: element,
      style: SATELLITE_STYLE,
      center: [-43.35, -21.76],
      zoom: 12,
      maxZoom: 19,
      attributionControl: false,
    });
    mapRef.current = map;
    const onDataLoading = () => setTilesLoading(true);
    const onIdle = () => setTilesLoading(false);
    const montar = () => {
      map.resize();
      garantirCamadas(map, basemapRef.current === 'satellite', mostrarInfoRef.current);
      aplicarConsulta(map, consultaRef.current, mostrarInfoRef.current);
      const alvo = geoRef.current;
      if (alvo && map.getSource('kmz')) {
        map.getSource('kmz').setData(alvo.collection);
        pintarIcones(map, alvo.collection);
        enquadrar(map, alvo);
      }
    };
    const fecharPopup = () => {
      if (popupRef.current) popupRef.current.remove();
    };
    const abrirPopup = (feature, lngLat) => {
      fecharPopup();
      const popup = new maplibregl.Popup({ closeButton: false, maxWidth: '360px', className: 'kmz-ponto-popup' })
        .setLngLat(lngLat)
        .setDOMContent(popupDaEstrutura(
          feature,
          () => popup.remove(),
          (ficha, indice) => abrirFotoRef.current(ficha, indice),
          (nsmpId) => abrirServicoRef.current(nsmpId),
          faixaRef.current,
        ))
        .addTo(map);
      popupRef.current = popup;
    };
    const featuresAtuais = () => {
      const alvo = geoRef.current;
      return (alvo && alvo.collection && alvo.collection.features) || [];
    };
    const noClique = (event) => {
      const feature = featureDoClique(map, event.point, featuresAtuais());
      if (!feature) {
        fecharPopup();
        return;
      }
      abrirPopup(feature, event.lngLat);
    };
    const noMouse = (event) => {
      map.getCanvas().style.cursor = featureDoClique(map, event.point, featuresAtuais()) ? 'pointer' : '';
    };
    map.on('dataloading', onDataLoading);
    map.on('idle', onIdle);
    map.on('load', montar);
    map.on('click', noClique);
    map.on('mousemove', noMouse);
    const observer = new ResizeObserver(() => {
      if (element.clientWidth > 0 && element.clientHeight > 0) map.resize();
    });
    observer.observe(element);
    return () => {
      observer.disconnect();
      map.off('dataloading', onDataLoading);
      map.off('idle', onIdle);
      map.off('click', noClique);
      map.off('mousemove', noMouse);
      fecharPopup();
      map.remove();
      mapRef.current = null;
    };
  }, [ativo]);

  useEffect(() => {
    const map = mapRef.current;
    if (!ativo || !map || !geo) return undefined;
    if (popupRef.current) popupRef.current.remove();
    setFoto(null);
    const aplicar = () => {
      garantirCamadas(map, basemapRef.current === 'satellite', mostrarInfoRef.current);
      aplicarConsulta(map, consultaRef.current, mostrarInfoRef.current);
      if (!map.getSource('kmz')) return;
      map.getSource('kmz').setData(geo.collection);
      pintarIcones(map, geo.collection);
      enquadrar(map, geo);
    };
    if (map.isStyleLoaded()) aplicar();
    else map.once('load', aplicar);
    return undefined;
  }, [ativo, geo]);

  useEffect(() => {
    const map = mapRef.current;
    if (!ativo || !map || !map.isStyleLoaded()) return undefined;
    const atualSatelite = Boolean(map.getSource('esri-imagery'));
    const querSatelite = basemap === 'satellite';
    if (atualSatelite === querSatelite) return undefined;
    const camera = {
      center: map.getCenter(),
      zoom: map.getZoom(),
      bearing: map.getBearing(),
      pitch: map.getPitch(),
    };
    map.setStyle(querSatelite ? SATELLITE_STYLE : STYLE_URL, { diff: false });
    map.once('style.load', () => {
      garantirCamadas(map, querSatelite, mostrarInfoRef.current);
      aplicarConsulta(map, consultaRef.current, mostrarInfoRef.current);
      const alvo = geoRef.current;
      if (alvo && map.getSource('kmz')) {
        map.getSource('kmz').setData(alvo.collection);
        pintarIcones(map, alvo.collection);
      }
      map.jumpTo(camera);
    });
    return undefined;
  }, [ativo, basemap]);

  useEffect(() => {
    const map = mapRef.current;
    if (!ativo || !map || !map.getLayer('kmz-rotulos')) return undefined;
    map.setLayoutProperty('kmz-rotulos', 'visibility', mostrarInfo ? 'visible' : 'none');
    aplicarConsulta(map, consultaRef.current, mostrarInfo);
    return undefined;
  }, [ativo, mostrarInfo]);

  useEffect(() => {
    const map = mapRef.current;
    if (!ativo || !map || !map.getLayer('kmz-linhas')) return undefined;
    aplicarConsulta(map, consulta, mostrarInfoRef.current);
    return undefined;
  }, [ativo, consulta, mostrarInfo]);

  useEffect(() => {
    const map = mapRef.current;
    if (!ativo || !map || !camera || !camera.bounds) return undefined;
    enquadrar(map, camera);
    return undefined;
  }, [ativo, camera]);

  useEffect(() => {
    if (!ativo) return undefined;
    const map = mapRef.current;
    if (!map) return undefined;
    const frame = requestAnimationFrame(() => map.resize());
    return () => cancelAnimationFrame(frame);
  }, [ativo, fullScreen]);

  const centralizar = () => {
    if (mapRef.current && geo) enquadrar(mapRef.current, geo);
  };

  return (
    <Box sx={{ position: 'absolute', inset: 0 }}>
      <GlobalStyles
        styles={{
          '.kmz-ponto-popup .maplibregl-popup-content': {
            padding: '12px 12px 14px',
            borderRadius: 8,
            whiteSpace: 'normal',
          },
        }}
      />
      <Box
        ref={containerRef}
        sx={{ width: '100%', height: '100%' }}
      />
      {geo ? (
        <KmzMapaControles
          edicao={false}
          trocaTrecho={false}
          modo="nenhum"
          onModo={() => {}}
          mostrarTudo={false}
          onMostrarTudo={() => {}}
          onMostrarSelecionado={() => {}}
          basemap={basemap}
          onBasemap={setBasemap}
          fullScreen={fullScreen}
          onToggleFullScreen={onToggleFullScreen}
          mostrarInfo={mostrarInfo}
          onMostrarInfo={() => setMostrarInfo((atual) => !atual)}
          onCentralizar={centralizar}
          onZoomIn={() => {
            const map = mapRef.current;
            if (map) map.zoomIn();
          }}
          onZoomOut={() => {
            const map = mapRef.current;
            if (map) map.zoomOut();
          }}
        />
      ) : null}
      {tilesLoading && geo ? (
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{
            position: 'absolute',
            left: 12,
            bottom: 28,
            zIndex: 2,
            bgcolor: 'background.paper',
            borderRadius: 1,
            px: 1.25,
            py: 0.75,
            boxShadow: 1,
          }}
        >
          <CircularProgress size={16} />
          <Typography variant="caption">Carregando o mapa</Typography>
        </Stack>
      ) : null}
      {foto && foto.ficha ? (
        <KmzFotoPainel
          ficha={foto.ficha}
          indice={foto.indice || 0}
          onIndice={(indice) => setFoto((atual) => (atual ? { ...atual, indice } : atual))}
          onClose={() => setFoto(null)}
        />
      ) : null}
      {geo && geo.truncado ? (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            position: 'absolute',
            left: 12,
            top: 12,
            zIndex: 2,
            bgcolor: 'background.paper',
            px: 1,
            py: 0.5,
            borderRadius: 1,
          }}
        >
          Mapa com os primeiros trechos do arquivo.
        </Typography>
      ) : null}
    </Box>
  );
};

KmzVisualizacaoMap.propTypes = {
  ativo: PropTypes.bool,
  geo: PropTypes.object,
  fullScreen: PropTypes.bool,
  onToggleFullScreen: PropTypes.func,
  nsirId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  consulta: PropTypes.object,
  camera: PropTypes.object,
  faixa: PropTypes.bool,
};

KmzVisualizacaoMap.defaultProps = {
  ativo: true,
  geo: null,
  fullScreen: false,
  onToggleFullScreen: () => {},
  nsirId: null,
  consulta: null,
  camera: null,
  faixa: false,
};

export default KmzVisualizacaoMap;
